import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Form, 
  Input, 
  Select, 
  Switch, 
  Space,
  Typography,
  Row,
  Col,
  App
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { videoApi } from '../services/videoApi';
import { websiteApi } from '../services/api';
import { CreateVideoDto } from '../types/video';
import { Website } from '../types/website';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const VideoForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  
  const isEdit = Boolean(id);

  // Fetch video data for editing
  const { data: video, isLoading: videoLoading } = useQuery(
    ['video', id],
    () => videoApi.getById(id!),
    { enabled: isEdit }
  );

  // Fetch websites for association
  const { data: websitesResponse, isLoading: websitesLoading } = useQuery(
    'websites',
    () => websiteApi.getAll({ limit: 1000 }) // Get all websites for selection
  );
  const websites: Website[] = websitesResponse?.data || [];

  const createMutation = useMutation(videoApi.create, {
    onSuccess: () => {
      message.success('Video created successfully!');
      queryClient.invalidateQueries('videos');
      navigate('/videos');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create video');
    },
  });

  const updateMutation = useMutation(
    (data: CreateVideoDto) => videoApi.update(id!, data),
    {
      onSuccess: () => {
        message.success('Video updated successfully!');
        queryClient.invalidateQueries('videos');
        queryClient.invalidateQueries(['video', id]);
        navigate('/videos');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to update video');
      },
    }
  );

  useEffect(() => {
    if (isEdit && video) {
      console.log('Setting form values for video:', video);
      console.log('Video websites:', video.websites);

      // Transform websites to IDs if they are objects
      const websiteIds = video.websites?.map((website: any) => {
        if (typeof website === 'string') {
          return website;
        } else if (website && website.id) {
          return website.id;
        } else if (website && website._id) {
          return website._id;
        }
        return website;
      }) || [];

      console.log('Transformed website IDs:', websiteIds);

      form.setFieldsValue({
        title: video.title,
        description: video.description,
        videoLink: video.videoLink,
        imageUrl: video.imageUrl,
        duration: video.duration,
        viewCount: video.viewCount,
        category: video.category,
        tags: video.tags?.join(', '),
        isPublished: video.isPublished,
        websites: websiteIds,
      });
    }
  }, [isEdit, video, form]);

  const handleSubmit = async (values: any) => {
    try {
      // Transform tags from string to array
      const tagsArray = values.tags 
        ? values.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag)
        : [];

      // Ensure websites is an array
      const websitesArray = Array.isArray(values.websites) ? values.websites : [];

      // Convert duration and viewCount from string to number
      const duration = values.duration ? parseInt(values.duration, 10) : 0;
      const viewCount = values.viewCount ? parseInt(values.viewCount, 10) : 0;

      const videoData: CreateVideoDto = {
        ...values,
        duration,
        viewCount,
        tags: tagsArray,
        websites: websitesArray,
      };

      if (isEdit) {
        await updateMutation.mutateAsync(videoData);
      } else {
        await createMutation.mutateAsync(videoData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleCancel = () => {
    navigate('/videos');
  };

  if (videoLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
          >
            Back to Videos
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {isEdit ? 'Edit Video' : 'Create New Video'}
          </Title>
        </Space>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isPublished: true,
            duration: 0,
            viewCount: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Title"
                rules={[
                  { required: true, message: 'Please enter video title' },
                  { min: 1, max: 200, message: 'Title must be between 1 and 200 characters' }
                ]}
              >
                <Input placeholder="Enter video title" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="videoLink"
                label="Video Link"
                rules={[
                  { required: true, message: 'Please enter video link' },
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
              >
                <Input placeholder="https://youtube.com/watch?v=..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="imageUrl"
                label="Thumbnail URL"
                rules={[
                  { required: true, message: 'Please enter thumbnail URL' },
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
              >
                <Input placeholder="https://example.com/thumbnail.jpg" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="duration"
                label="Duration (seconds)"
                rules={[
                  { required: true, message: 'Please enter duration' },
                  { 
                    pattern: /^\d+$/, 
                    message: 'Duration must be a valid number' 
                  },
                  { 
                    validator: (_, value) => {
                      if (value && value !== '') {
                        const num = parseInt(value);
                        if (isNaN(num) || num < 1) {
                          return Promise.reject(new Error('Duration must be at least 1 second'));
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input type="number" placeholder="180" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="viewCount"
                label="View Count"
                rules={[
                  { 
                    pattern: /^\d+$/, 
                    message: 'View count must be a valid number' 
                  },
                  { 
                    validator: (_, value) => {
                      if (value && value !== '') {
                        const num = parseInt(value);
                        if (isNaN(num) || num < 0) {
                          return Promise.reject(new Error('View count must be 0 or greater'));
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
              >
                <Input placeholder="Tutorial, Demo, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} placeholder="Enter video description" />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags (comma separated)"
          >
            <Input placeholder="tutorial, web-development, html" />
          </Form.Item>

          <Form.Item
            name="websites"
            label="Associated Websites"
          >
            <Select
              mode="multiple"
              placeholder={websitesLoading ? "Loading websites..." : "Select websites"}
              allowClear
              loading={websitesLoading}
              notFoundContent={websitesLoading ? "Loading..." : "No websites found"}
            >
              {websites.map((website: Website) => (
                <Option key={website.id} value={website.id}>
                  {website.name} ({website.domain})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isPublished"
            label="Published"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {isEdit ? 'Update Video' : 'Create Video'}
              </Button>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default VideoForm;
